"use client";

import { c as _c } from "react/compiler-runtime";
import { BellRing, CalendarClock, Download, Images, Megaphone, MonitorPlay, PartyPopper, RefreshCw, Smartphone, Ticket, type LucideIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import Reveal from "@/components/index/Reveal";
type BenefitKey = "reminders" | "originals" | "retries" | "later";

// The left column slides in from the left and the right column from the
// right, each point a little later and from a little further than the one
// above it. Phones get a short rise instead of a sideways slide.
const BENEFITS: {
  key: BenefitKey;
  Icon: LucideIcon;
  revealClassName: string;
  delay: number;
}[] = [{
  key: "reminders",
  Icon: BellRing,
  revealClassName: "md:[--reveal-x:-32px] md:[--reveal-y:0px]",
  delay: 0
}, {
  key: "originals",
  Icon: Download,
  revealClassName: "md:[--reveal-x:-44px] md:[--reveal-y:0px]",
  delay: 110
}, {
  key: "retries",
  Icon: RefreshCw,
  revealClassName: "md:[--reveal-x:38px] md:[--reveal-y:0px]",
  delay: 60
}, {
  key: "later",
  Icon: CalendarClock,
  revealClassName: "md:[--reveal-x:46px] md:[--reveal-y:0px]",
  delay: 170
}];
interface BenefitsCardProps {
  /** A mockup of today's guest screens (PhoneMockups), rendered on the server. */
  phone: React.ReactNode;
}
export default function BenefitsCard(t0) {
  const $ = _c(37);
  const {
    phone
  } = t0;
  const t = useTranslations("Index");
  const tGuest = useTranslations("HowItWorks");
  let t1;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = _temp;
    t2 = _temp2;
    t3 = _temp3;
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t1 = $[0];
    t2 = $[1];
    t3 = $[2];
  }
  let renderFact;
  let t4;
  let t5;
  if ($[3] !== t) {
    const textSettings = {
      pstrong: t1,
      p: t2,
      h2: t3
    };
    renderFact = _temp4;
    t4 = t.rich("6point.point2", textSettings);
    t5 = t.rich("6point.point3", textSettings);
    $[3] = t;
    $[4] = renderFact;
    $[5] = t4;
    $[6] = t5;
  } else {
    renderFact = $[4];
    t4 = $[5];
    t5 = $[6];
  }
  let t6;
  let t7;
  if ($[7] !== renderFact || $[8] !== tGuest) {
    t6 = renderFact(tGuest("Facts.retries"), tGuest("FAQ.Item2.body"));
    t7 = renderFact(tGuest("Facts.later"), tGuest("FAQ.Item6.body"));
    $[7] = renderFact;
    $[8] = tGuest;
    $[9] = t6;
    $[10] = t7;
  } else {
    t6 = $[9];
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t4 || $[12] !== t5 || $[13] !== t6 || $[14] !== t7) {
    const content = {
      reminders: t4,
      originals: t5,
      retries: t6,
      later: t7
    };
    t8 = t9 => {
      const {
        key,
        Icon,
        revealClassName,
        delay
      } = t9;
      return <Reveal key={key} className={`group flex gap-3 ${revealClassName}`} delay={delay}><span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gg-orange-100 text-[#9A5A00] transition-transform duration-500 ease-[var(--ease-overshoot)] can-hover:group-hover:-rotate-[8deg] can-hover:group-hover:scale-110"><Icon aria-hidden="true" className="h-5 w-5" /></span><div className="flex flex-col gap-2">{content[key]}</div></Reveal>;
    };
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const renderBenefit = t8;
  let t9;
  if ($[16] !== t) {
    t9 = t("6point.title");
    $[16] = t;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t9) {
    t10 = <h2 className="text-center text-3xl font-bold tracking-tight md:text-4xl">{t9}</h2>;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== renderBenefit) {
    t11 = BENEFITS.slice(0, 2).map(renderBenefit);
    $[20] = renderBenefit;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t11) {
    t12 = <div className="grid gap-10 md:gap-16">{t11}</div>;
    $[22] = t11;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== phone) {
    t13 = <Reveal className="mx-auto flex justify-center" y={56} rotate={-5} scale={0.94} delay={80} duration={900}>{phone}</Reveal>;
    $[24] = phone;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== renderBenefit) {
    t14 = BENEFITS.slice(2).map(renderBenefit);
    $[26] = renderBenefit;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t14) {
    t15 = <div className="grid gap-10 md:gap-16">{t14}</div>;
    $[28] = t14;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== t12 || $[31] !== t13 || $[32] !== t15) {
    t16 = <div className="mx-4 grid grid-cols-1 items-center gap-10 md:grid-cols-[1fr_auto_1fr] md:gap-12">{t12}{t13}{t15}</div>;
    $[30] = t12;
    $[31] = t13;
    $[32] = t15;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== t10 || $[35] !== t16) {
    t17 = <div className="w-full max-w-screen-lg lg:mx-auto place-items-center grid gap-10 md:gap-20 items-center p-4 md:p-0">{t10}{t16}</div>;
    $[34] = t10;
    $[35] = t16;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  return t17;
}

// The Christmas party page's six points, in the same order as its copy.
function _temp4(title, body) {
  return <><p className="font-bold">{title}</p><p>{body}</p></>;
}
function _temp3(children_1) {
  return <h2 className="font-bold text-lg">{children_1}</h2>;
}
function _temp2(children_0) {
  return <p>{children_0}</p>;
}
function _temp(children) {
  return <p className="font-bold">{children}</p>;
}
const CHRISTMAS_BENEFITS: {
  point: number;
  Icon: LucideIcon;
}[] = [{
  point: 1,
  Icon: Ticket
}, {
  point: 2,
  Icon: MonitorPlay
}, {
  point: 3,
  Icon: Smartphone
}, {
  point: 4,
  Icon: Megaphone
}, {
  point: 5,
  Icon: Images
}, {
  point: 6,
  Icon: PartyPopper
}];
const CHRISTMAS_REVEALS = [{
  className: "md:[--reveal-x:-32px] md:[--reveal-y:0px]",
  delay: 0
}, {
  className: "md:[--reveal-x:-44px] md:[--reveal-y:0px]",
  delay: 90
}, {
  className: "md:[--reveal-x:-38px] md:[--reveal-y:0px]",
  delay: 170
}, {
  className: "md:[--reveal-x:36px] md:[--reveal-y:0px]",
  delay: 60
}, {
  className: "md:[--reveal-x:46px] md:[--reveal-y:0px]",
  delay: 140
}, {
  className: "md:[--reveal-x:40px] md:[--reveal-y:0px]",
  delay: 230
}] as const;
export function BenefitsCardChristmas(t0) {
  const $ = _c(30);
  const {
    phone
  } = t0;
  const t = useTranslations("landingPages.julefrokost.6point");
  let t1;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = _temp5;
    t2 = _temp6;
    t3 = _temp7;
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t1 = $[0];
    t2 = $[1];
    t3 = $[2];
  }
  let t10;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[3] !== phone || $[4] !== t) {
    const textSettings = {
      pstrong: t1,
      p: t2,
      h2: t3
    };
    const renderBenefit = t11 => {
      const {
        point,
        Icon
      } = t11;
      const reveal = CHRISTMAS_REVEALS[point - 1];
      return <Reveal key={point} className={`group flex gap-3 ${reveal.className}`} delay={reveal.delay}><span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#FDEEEE] text-gg-christmas-red transition-transform duration-500 ease-[var(--ease-overshoot)] can-hover:group-hover:-rotate-[8deg] can-hover:group-hover:scale-110"><Icon aria-hidden="true" className="h-5 w-5" /></span><div className="flex flex-col gap-2">{t.rich(`point${point}`, textSettings)}</div></Reveal>;
    };
    t9 = "w-full max-w-screen-lg lg:mx-auto place-items-center grid gap-10 md:gap-20 items-center p-4 md:p-0";
    let t12;
    if ($[12] !== t) {
      t12 = t("title");
      $[12] = t;
      $[13] = t12;
    } else {
      t12 = $[13];
    }
    if ($[14] !== t12) {
      t10 = <h2 className="text-center text-3xl font-bold tracking-tight md:text-4xl">{t12}</h2>;
      $[14] = t12;
      $[15] = t10;
    } else {
      t10 = $[15];
    }
    t6 = "mx-4 grid grid-cols-1 items-center gap-10 md:grid-cols-[1fr_auto_1fr] md:gap-12";
    t7 = <div className="grid gap-10 md:gap-14">{CHRISTMAS_BENEFITS.slice(0, 3).map(renderBenefit)}</div>;
    if ($[16] !== phone) {
      t8 = <Reveal className="mx-auto flex justify-center" y={56} rotate={-5} scale={0.94} delay={80} duration={900}>{phone}</Reveal>;
      $[16] = phone;
      $[17] = t8;
    } else {
      t8 = $[17];
    }
    t4 = "grid gap-10 md:gap-14";
    t5 = CHRISTMAS_BENEFITS.slice(3).map(renderBenefit);
    $[3] = phone;
    $[4] = t;
    $[5] = t10;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
    $[11] = t9;
  } else {
    t10 = $[5];
    t4 = $[6];
    t5 = $[7];
    t6 = $[8];
    t7 = $[9];
    t8 = $[10];
    t9 = $[11];
  }
  let t11;
  if ($[18] !== t4 || $[19] !== t5) {
    t11 = <div className={t4}>{t5}</div>;
    $[18] = t4;
    $[19] = t5;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t11 || $[22] !== t6 || $[23] !== t7 || $[24] !== t8) {
    t12 = <div className={t6}>{t7}{t8}{t11}</div>;
    $[21] = t11;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t10 || $[27] !== t12 || $[28] !== t9) {
    t13 = <div className={t9}>{t10}{t12}</div>;
    $[26] = t10;
    $[27] = t12;
    $[28] = t9;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  return t13;
}
function _temp7(children_1) {
  return <h2 className="font-bold text-lg">{children_1}</h2>;
}
function _temp6(children_0) {
  return <p>{children_0}</p>;
}
function _temp5(children) {
  return <p className="font-bold">{children}</p>;
}